/**
 * Utility functions for exporting data to various formats
 */

/**
 * Formats a value for CSV export, handling different data types
 * @param {any} value - The value to format
 * @returns {String} - Formatted string value
 */
const formatValueForCSV = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  
  // Handle objects and arrays by converting to JSON string
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  }
  
  // Handle other types by converting to string
  return String(value);
};

/**
 * Formats health score weightage into a readable string
 * @param {Array} weightage - Array of health score weightage objects
 * @returns {String} - Formatted string like "Heart Health(25), Cognition and Memory(5)"
 */
const formatHealthScoreWeightage = (weightage) => {
  if (!weightage || !Array.isArray(weightage) || weightage.length === 0) {
    return '';
  }
  
  return weightage
    .filter(item => item && item.name && item.weight)
    .map(item => `${item.name}(${item.weight})`)
    .join(', ');
};

/**
 * Formats a range as "min - max"
 * @param {Number|String} min - Minimum value
 * @param {Number|String} max - Maximum value
 * @returns {String} - Formatted range string
 */
const formatRange = (min, max) => {
  if ((min === null || min === undefined || min === '') && 
      (max === null || max === undefined || max === '')) {
    return '';
  }
  
  if (min === null || min === undefined || min === '') {
    return `- ${max}`;
  }
  
  if (max === null || max === undefined || max === '') {
    return `${min} -`;
  }
  
  return `${min} - ${max}`;
};

/**
 * Converts an array of objects to CSV format and triggers a download
 * @param {Array} data - Array of objects to convert to CSV
 * @param {String} filename - Name of the file to download (without extension)
 * @param {Array} [customHeaders] - Optional array of header names to use instead of object keys
 */
export const exportToCSV = (data, filename, customHeaders = null) => {
  if (!data || !data.length) {
    console.error('No data to export');
    return;
  }

  // Define the headers we want in the specific order for the spreadsheet
  const predefinedHeaders = [
    'test_code',
    'biomarker_name',
    'unit',
    'gender',
    'age_range',
    'hormonal_phase',
    'normal_range',
    'optimal_range',
    'mildly_decreased_range',
    'significantly_decreased_range',
    'mildly_increased_range',
    'significantly_increased_range',
    'description',
    'impact_on_health_decreased',
    'impact_on_health_increased',
    'suggestions_to_improve_when_decreased',
    'suggestions_to_improve_when_increased',
    'followuptime_mildly_deranged',
    'followuptime_significantly_deranged',
    'category',
    'health_score_weightage',
    'test_name',
  ];

  // Create a mapping of display headers
  const headerMapping = {
    'test_code': 'Code',
    'biomarker_name': 'Biomarker_Name',
    'unit': 'Unit',
    'gender': 'Gender',
    'age_range': 'Age_Range',
    'hormonal_phase': 'Hormonal_Phase',
    'normal_range': 'Normal_Range',
    'optimal_range': 'Optimal_Range',
    'mildly_decreased_range': 'Mildly_Decreased_Range',
    'significantly_decreased_range': 'Significantly_Decreased_Range',
    'mildly_increased_range': 'Mildly_Increased_Range',
    'significantly_increased_range': 'Significantly_Increased_Range',
    'description': 'Description',
    'impact_on_health_decreased': 'Impact_on_Health_Decreased',
    'impact_on_health_increased': 'Impact_on_Health_Increased',
    'suggestions_to_improve_when_decreased': 'Suggestions_to_Improve_When_Decreased',
    'suggestions_to_improve_when_increased': 'Suggestions_to_Improve_When_Increased',
    'followuptime_mildly_deranged': 'Followuptime_Mildly_Deranged',
    'followuptime_significantly_deranged': 'Followuptime_Significantly_Deranged',
    'category': 'Category',
    'health_score_weightage': 'Health_Score_Weightage',
    'test_name': 'Map_to_Test'
  };
  
  // Use the display headers for the CSV
  const displayHeaders = predefinedHeaders.map(header => headerMapping[header] || header);
  
  // Create CSV rows
  const csvRows = [];
  
  // Add header row
  csvRows.push(displayHeaders.join(','));
  
  // Add data rows
  for (const row of data) {
    const processedRow = {};
    
    // Process all the range fields
    processedRow['normal_range'] = formatRange(row.normal_range_min, row.normal_range_max);
    processedRow['optimal_range'] = formatRange(row.optimal_range_min, row.optimal_range_max);
    processedRow['mildly_decreased_range'] = formatRange(row.mildly_decreased_min, row.mildly_decreased_max);
    processedRow['significantly_decreased_range'] = formatRange(row.significantly_decreased_min, row.significantly_decreased_max);
    processedRow['mildly_increased_range'] = formatRange(row.mildly_increased_min, row.mildly_increased_max);
    processedRow['significantly_increased_range'] = formatRange(row.significantly_increased_min, row.significantly_increased_max);
    processedRow['age_range'] = formatRange(row.agerange_min, row.agerange_max);
    
    // Copy over the non-range fields
    processedRow['test_code'] = row.test_code;
    processedRow['description'] = row.description;
    processedRow['gender'] = row.gender;
    processedRow['hormonal_phase'] = row.hormonal_phase;
    processedRow['unit'] = row.unit;
    processedRow['test_name'] = row.test_name;
    processedRow['biomarker_name'] = row.biomarker_name;
    processedRow['impact_on_health_decreased'] = row.impact_on_health_decreased;
    processedRow['impact_on_health_increased'] = row.impact_on_health_increased;
    processedRow['suggestions_to_improve_when_decreased'] = row.suggestions_to_improve_when_decreased;
    processedRow['suggestions_to_improve_when_increased'] = row.suggestions_to_improve_when_increased;
    processedRow['followuptime_mildly_deranged'] = row.followuptime_mildly_deranged;
    processedRow['followuptime_significantly_deranged'] = row.followuptime_significantly_deranged;
    
    // Special handling for health_score_weightage
    processedRow['health_score_weightage'] = formatHealthScoreWeightage(row.health_score_weightage);
    
    // Special handling for clinical_data_category
    if (row.clinical_data_category && row.clinical_data_category.profile_name) {
      processedRow['category'] = row.clinical_data_category.profile_name;
    } else {
      processedRow['category'] = row.category;
    }
    
    // Create the CSV row
    const values = predefinedHeaders.map(header => {
      const value = formatValueForCSV(processedRow[header] || '');
      // Escape quotes and wrap in quotes
      const escaped = value.replace(/"/g, '""');
      return `"${escaped}"`;
    });
    
    csvRows.push(values.join(','));
  }
  
  // Combine rows into a single CSV string
  const csvString = csvRows.join('\n');
  
  // Create a Blob and download link
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  
  // Set link properties
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  // Add to document, trigger download, and clean up
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up the URL object
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);
};
